import * as Yup from "yup";
import {
  useWSSnackbar,
  WSActions,
  WSDivider,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInputText,
  WSText,
  WSTooltip
} from "@wingspanhq/fe-component-library";

import { Payer } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayers } from "@query";
import {
  contactCompanyValidator,
  validatorFirstName,
  validatorLastName
} from "../../../../utils/validators";
import { validatorEmail } from "../../../../shared/validators/validatorEmail";
import { ErrorAlert } from "@components/ErrorAlert";
import { AdditionalEmailsField } from "../../../../Invoices/components/AdditionalEmailsField/AdditionalEmailsField";
import { useRef } from "react";

export interface EditClientModalProps extends WSElementProps {
  client: Payer;
  onSuccess?: (client: Payer) => void;
  onClose: () => void;
}

type FormData = {
  company: string;
  firstName: string;
  lastName: string;
  emailsCC: { email: string }[];
};

export const EditClientModal: React.FC<EditClientModalProps> = ({
  client,
  onSuccess,
  onClose,
  ...elementProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const tooltipIconRef = useRef<HTMLElement>(null);

  // v3 write: updatePayer. Auto-invalidates getPayer/listPayers via the registry,
  // so no manual cache priming is needed.
  const [updateClient, updateClientMeta] =
    queriesPayers.updatePayer.useMutation();

  return (
    <WSElement {...elementProps}>
      <WSDivider mb="XL" />
      <WSForm<FormData>
        defaultValues={{
          company: client.profile?.company?.doingBusinessAs || "",
          // TODO (v3-migration): the v3 read profile exposes only displayName +
          // company (no individual first/last), so the name fields cannot be
          // pre-filled from v3 today — they start empty and write on save.
          firstName: "",
          lastName: "",
          emailsCC: (client.notificationCcEmails || []).map(email => ({
            email
          }))
        }}
        onSubmit={values => {
          // RHF omits the field array entirely when no recipient rows were
          // added, so the submitted value can be undefined.
          const notificationCcEmails = (values.emailsCC || [])
            .map(item => item.email.trim())
            .filter(Boolean);
          updateClient(
            {
              payerId: client.id as string,
              payerUpdate: {
                profile: {
                  company: { doingBusinessAs: values.company },
                  individual:
                    values.firstName || values.lastName
                      ? {
                          firstName: values.firstName,
                          lastName: values.lastName
                        }
                      : undefined
                },
                // Invoice CC recipients — now a first-class v3 Payer field
                // (`notificationCcEmails`, WIN-15229). An empty array explicitly
                // clears the stored list; omitting would preserve it.
                notificationCcEmails
              }
            },
            {
              onSuccess: () => {
                onSuccess?.(client);
                onClose();
                openSnackbar({
                  duration: 5000,
                  type: "success",
                  message: "Client updated successfully"
                });
              }
            }
          );
        }}
        validationSchema={Yup.object().shape(
          {
            company: Yup.string().when("firstName", {
              is: (firstName: string) => firstName?.length > 0,
              then: contactCompanyValidator,
              otherwise: contactCompanyValidator.required(
                "At least one of company or name must be provided"
              )
            }),
            firstName: Yup.string().when("company", {
              is: (company: string) => company?.length > 0,
              then: validatorFirstName,
              otherwise: validatorFirstName.required(
                "At least one of company or name must be provided"
              )
            }),
            lastName: validatorLastName,
            // The client's own email isn't a field on this form (it isn't
            // editable here), so the "same as the client email" rule compares
            // against the stored value rather than a sibling field the way
            // AddClientModal's `notEqualToField` does.
            emailsCC: Yup.array(
              Yup.object().shape({
                email: validatorEmail.notOneOf(
                  [client.email].filter(Boolean),
                  "Additional email can't be the same as the client email"
                )
              })
            ).uniqueByProperty("Additional email must be unique", "email")
          },
          [["company", "firstName"]]
        )}
      >
        <WSText.ParagraphSm color="gray500">
          Client company or name *
        </WSText.ParagraphSm>
        <WSText.ParagraphXs color="gray400">
          At least one of these fields must be provided
        </WSText.ParagraphXs>
        <WSForm.Field
          mt="XL"
          name="company"
          label="Client company"
          component={WSInputText}
          mb="XL"
        />
        <WSForm.Field
          name="firstName"
          label="Client first name"
          component={WSInputText}
          mb="XL"
        />
        <WSForm.Field
          name="lastName"
          label="Client last name"
          component={WSInputText}
        />

        <WSDivider my="2XL" />

        <WSFlexBox.CenterY mb="XS">
          <WSText.ParagraphSm color="gray500" mr="XS">
            Client email
          </WSText.ParagraphSm>
          <WSTooltip dark target={tooltipIconRef}>
            Client email can not be changed
          </WSTooltip>
          <WSIcon
            ref={tooltipIconRef}
            name="info-circle-fill"
            color="gray600"
            size="S"
          />
        </WSFlexBox.CenterY>
        <WSText.ParagraphSm weight="book" mb="XL">
          {client.email}
        </WSText.ParagraphSm>

        <WSDivider mb="XL" />

        <WSText.ParagraphSm color="gray500" mb="XL">
          Contact email CC
        </WSText.ParagraphSm>
        <AdditionalEmailsField
          name="emailsCC"
          inputLabel="Contact email CC"
          buttonLabel="Add email recipient"
          buttonDescription="Additional email addresses will be cc’d"
        />

        <ErrorAlert
          mb="XL"
          error={updateClientMeta.error}
          context="UpdateClient"
        />

        <WSDivider my="2XL" />

        <WSActions
          alignment="fill"
          buttons={[
            {
              name: "updateClient",
              type: "submit",
              kind: "Primary",
              loading: updateClientMeta.isLoading,
              label: "Save changes"
            },
            {
              name: "cancel",
              type: "button",
              kind: "Tertiary",
              label: "Cancel",
              onClick: onClose
            }
          ]}
        />
      </WSForm>
    </WSElement>
  );
};
